import { inject, Injectable, OnInit, signal } from '@angular/core';
import { } from '@angular/core/rxjs-interop';
import {
  Auth, FacebookAuthProvider,
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
  signInWithEmailAndPassword,
  User,
  authState,
} from '@angular/fire/auth';
import { MERCHANT_FIRESTORE_SERVICE, USER_FIRESTORE_SERVICE } from '../utils/fire-core.tokens';
import { firstValueFrom, map, of, switchMap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AuthService {

  private readonly auth = inject(Auth);
  private readonly merchantFireService = inject(MERCHANT_FIRESTORE_SERVICE);
  private readonly userFireService = inject(USER_FIRESTORE_SERVICE);

  private state$ = authState(this.auth)
    .pipe(
      map(user => {
        return user;
      })
    );

  private merchant$ = this.state$
    .pipe(
      switchMap(_user => {
        return _user ?
          this.merchantFireService.ref.fetchById(_user.uid) :
          of(null);
      })
    );

  private user$ = this.state$
    .pipe(
      switchMap(_user => {
        return _user ?
          this.userFireService.ref.fetchById(_user.uid) :
          of(null);
      })
    );

  get ref() {
    return {
      state$: this.state$,
      merchant$: this.merchant$,
      snapshot: {
        state: firstValueFrom(this.state$),
        merchant: firstValueFrom(this.merchant$),
      }
    }
  }

  async loginWithEmailAndPassword(loginForm: { email: string, password: string }) {
    try {
      const data = await signInWithEmailAndPassword(this.auth, loginForm.email, loginForm.password);
      await this.createMerchant(data.user);
    } catch (error) {
      throw this.handleError(error)
    }
  }

  async loginWithGoogle() {
    try {
      const data = await signInWithPopup(this.auth, new GoogleAuthProvider());
      await this.createMerchant(data.user);
      await this.createUser(data.user);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async loginWithFacebook() {
    try {
      const data = await signInWithPopup(this.auth, new FacebookAuthProvider());
      await this.createMerchant(data.user);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async logout() {
    try {
      await signOut(this.auth);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  private async createMerchant(user: User) {
    const _user = await firstValueFrom(this.merchant$);
    if (!_user)
      await this.merchantFireService.ref.save({
        uid: user.uid,
        email: user.email,
        emailVerified: user.emailVerified,
        metadata: {
          createdAt: new Date(user.metadata.creationTime!)
        },
        ...(user.displayName && { displayName: user.displayName }),
        ...(user.phoneNumber && { phoneNumber: user.phoneNumber }),
        ...(user.photoURL && { photoURL: user.photoURL })
      });
  }

  private async createUser(user: User) {
    const _user = await firstValueFrom(this.user$);
    if (!_user)
      await this.userFireService.ref.save({
        uid: user.uid,
        email: user.email,
        emailVerified: user.emailVerified,
        metadata: {
          createdAt: new Date(user.metadata.creationTime!)
        },
        ...(user.displayName && { displayName: user.displayName }),
        ...(user.phoneNumber && { phoneNumber: user.phoneNumber }),
        ...(user.photoURL && { photoURL: user.photoURL })
      });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private handleError(error: any) {
    const errMessage = error?.message;
    if (errMessage === undefined) return error;
    if (typeof errMessage !== 'string') return error;
    return {
      ...error,
      message: errMessage
        .replace(/ *\([^)]*\) */g, '')
        .replace('..', '.')
        .replace('Firebase: ', '')
    };
  }

}